import {
  Button,
  Col,
  Descriptions,
  Drawer,
  Dropdown,
  Flex,
  Grid,
  Message,
  Modal,
  Row,
  Tag,
  Tooltip,
  Typography,
} from "@payping/web-components";
import { useToggle } from "react-use";

import { toJalali } from "@payping/utils/dateUtils";
import { getCorrectDate } from "@/utils";
import useTranslator from "@/shared/translate/translator";
import { JALALI_DATE_TIME_FORMAT } from "@/shared/constants/date.constant";
import { RefundInfo, RefundOperationStateEnum } from "@/refund/repository/refund/v1/refund-v1-api";
import { REFUND_STATE_COLOR_DICTIONARY, WAGE_PAYER_DICTIONARY } from "@/refund/constants/dictionary.constant";
import { useCancelRefund } from "@/refund/use-cases";
import { CuToman } from "@payping/icons";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";

type Props = {
  data: RefundInfo | null;
  onCloseClicked: () => void;
  onRefundCancelled?: () => void;
};

const RefundTransactionDetails: React.FC<Props> = ({ data, onCloseClicked, onRefundCancelled }) => {
  const { t } = useTranslator("refund.transactions.details");
  const cancelRefund = useCancelRefund();
  const [isConfirmModalOpen, toggleConfirmModal] = useToggle(false);

  const { md } = Grid.useBreakpoint();
  const isOpen = Boolean(data);

  const handleMenuClick = () => {
    toggleConfirmModal();
  };

  const handleConfirm = async () => {
    if (data?.code) {
      try {
        await cancelRefund.mutateAsync(data.code);
        Message.success({ content: t("actions.cancelRefundSuccess") });
        onRefundCancelled?.();
        toggleConfirmModal();
      } catch (error) {
        Message.error({ content: t("actions.cancelRefundError") });
      }
    }
  };

  const menuItems = [
    {
      key: "cancel",
      label: t("actions.cancelRefund"),
      onClick: handleMenuClick,
    },
  ];
  const getTooltipTitle = (state?: number) => {
    switch (state) {
      case RefundOperationStateEnum.Cancelled:
        return t("tooltips.refund_canceled");
      case RefundOperationStateEnum.ProcessingRefund:
        return t("tooltips.sent_to_bank");
      case RefundOperationStateEnum.RefundFailed:
        return t("tooltips.refund_failed");
      case RefundOperationStateEnum.RefundSucceeded:
        return t("tooltips.refund_completed");
      default:
        return undefined;
    }
  };

  const disabledStates = [
    RefundOperationStateEnum.Cancelled,
    RefundOperationStateEnum.Created,
    RefundOperationStateEnum.ProcessingRefund,
    RefundOperationStateEnum.RefundFailed,
    RefundOperationStateEnum.RefundSucceeded,
  ];

  if (isOpen && data)
    return (
      <>
        <Drawer
          open={isOpen}
          extra={
            <Tooltip title={getTooltipTitle(data.state)}>
              <span>
                <Dropdown
                  menu={{ items: menuItems }}
                  trigger={["click"]}
                  disabled={disabledStates.includes(data.state ?? -1)}
                >
                  <Button type="default" disabled={disabledStates.includes(data.state ?? -1)}>
                    {t("actions.title")}
                  </Button>
                </Dropdown>
              </span>
            </Tooltip>
          }
          title={t("title")}
          placement={md ? "left" : "bottom"}
          onClose={onCloseClicked}
          height={"100%"}
          width={md ? 528 : "100%"}
        >
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Descriptions
                column={1}
                bordered
                title={
                  <Flex wrap align="center" gap={"small"}>
                    <Typography.Text>{t("subtitle")}</Typography.Text>
                    <Tag color={data.state ? REFUND_STATE_COLOR_DICTIONARY[data.state] : "default"}>
                      {data.localizedState}
                    </Tag>
                  </Flex>
                }
              >
                <Descriptions.Item label={t("amount")}>
                  <Flex align="center">
                    <Typography.Text type="secondary">{`${formatLocalizedNumber({ value: data.requestedAmount ?? 0 })}`}</Typography.Text>
                    <CuToman width={24} height={24} />
                  </Flex>
                </Descriptions.Item>

                <Descriptions.Item label={t("transferReference")}>{data.transferReference || "-"}</Descriptions.Item>
                <Descriptions.Item label={t("creationDateTime")}>
                  {toJalali({ date: getCorrectDate(data.creationDateTime ?? ""), toFormat: JALALI_DATE_TIME_FORMAT })
                    .string || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("successDateTime")}>
                  {toJalali({ date: getCorrectDate(data.successDateTime ?? ""), toFormat: JALALI_DATE_TIME_FORMAT })
                    .string || "-"}
                </Descriptions.Item>
                <Descriptions.Item label={t("code")}>{data.code}</Descriptions.Item>
                <Descriptions.Item label={t("paymentCode")}>{data.paymentCode ?? ""}</Descriptions.Item>
                <Descriptions.Item label={t("refundWage")}>
                  <Flex align="center">
                    <Typography.Text type="secondary">
                      {formatLocalizedNumber({ value: data.refundWage ?? 0 })}
                    </Typography.Text>
                    <CuToman width={24} height={24} />
                  </Flex>
                </Descriptions.Item>
                <Descriptions.Item label={t("wagePayer")}>
                  {data.wagePayer ? t(WAGE_PAYER_DICTIONARY[data.wagePayer]) : ""}
                </Descriptions.Item>
              </Descriptions>
            </Col>
          </Row>
        </Drawer>

        <Modal
          open={isConfirmModalOpen}
          title={t("actions.confirmModal.title")}
          onOk={handleConfirm}
          onCancel={toggleConfirmModal}
          okText={t("actions.confirmModal.okText")}
          cancelText={t("actions.confirmModal.cancelText")}
          confirmLoading={cancelRefund.isPending}
        >
          <p>{t("actions.confirmModal.content")}</p>
        </Modal>
      </>
    );
  return "";
};

export default RefundTransactionDetails;
